import { TeacherRoomManager } from "@/manager/room/teacher.manager";
import { RoomSessionModel, HelperModel, TeachingMode } from "@/models";
import { GLError } from "@/models/error.model";
import { UserModel } from "@/models/user.model";
import { MutationTree } from "vuex";
import { ParamsToJoinCurSessionInterface, UserIdPayload } from "../interface";
import { ClassAction, ClassActionFromValue } from "../student/state";
import { initState, TeacherRoomState } from "./state";
import { cloneDeep } from "lodash";
import { RoomOptions } from "@/manager/room/base.manager";
import { defineVuexName, MODULES } from "@/store/utils";
import { TeacherUpdateStudentsBadgeEventData } from "@/ws";

type State = TeacherRoomState;

const mutations: MutationTree<State> = {
  //endClass
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.END_CLASS)](s: State): void {
    s.manager?.close(true);
    Object.assign(s, initState());
  },
  //leaveClass
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.LEAVE_CLASS)](s: State): void {
    s.manager?.close(false);
    Object.assign(s, initState());
  },
  //setTeachingMode
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_TEACHING_MODE)](state: State, mode: TeachingMode): void {
    state.teachingMode = mode;
  },
  //setIsConnected
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_IS_CONNECTED)](state: State, isConnected: boolean): void {
    state.isConnected = isConnected;
    if (!isConnected) {
      state.isRealtimeReady = false;
    }
  },
  //setIsRealtimeReady
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_IS_REALTIME_READY)](state: State, isRealtimeReady: boolean): void {
    state.isRealtimeReady = isRealtimeReady;
  },
  //setError
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_ERROR)](s: State, p: GLError | null): void {
    s.error = p;
  },
  //setUser
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_USER)](s: State, p: UserModel): void {
    s.user = p;
  },
  //setRoomInfo
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_ROOM_INFO)](s: State, p: RoomSessionModel): void {
    const isActionUpToDate = p.options?.isActionUpToDate;
    const isHelper = p.options?.isHelper;
    const blockTeacherUpdate = isActionUpToDate && !isHelper;
    const blockHelperUpdate = isActionUpToDate && isHelper;
    s.info = p;
    s.idOne = p.oneAndOneDto?.studentId ? p.oneAndOneDto?.studentId : "";
    s.currentLesson = p.classInfo?.lesson;
    s.currentUnit = p.classInfo?.unit;
    // Skip updating teacher info if current user is joining as a teacher
    // This prevents overwriting local teacher state with potentially stale data from server
    if (!blockTeacherUpdate) {
      s.teacher = {
        id: p.teacher.id,
        name: p.teacher.name,
        avatar: s.teacher?.avatar || "",
        videoCallStatus: p.teacher.videoCallStatus,
        disconnectTime: p.teacher.disconnectTime ? Date.now() - p.teacher.disconnectTime : null,
      };
    }
    if (p.helper) {
      // Skip updating helper info if current user is joining as a helper
      // This prevents overwriting local helper state with potentially stale data from server
      if (!blockHelperUpdate) {
        s.helper = {
          id: p.helper.id,
          name: p.helper.name,
          avatar: s.helper?.avatar || "",
          videoCallStatus: p.helper.videoCallStatus,
          isVideoShownByTeacher: p.helper.isVideoShownByTeacher,
        };
      }
    } else {
      s.helper = undefined;
    }
    s.teachingMode = p.state.teachingMode;
    s.students = p.students.map((st, index) => {
      return {
        id: st.id,
        name: st.name,
        englishName: st.englishName,
        avatar: "",
        badge: st.badge,
        videoCallStatus: st.videoCallStatus,
        index: index,
        raisingHand: st.isRaisingHand,
        isPalette: st.isPalette,
      };
    });
    // If joining as a teacher, create a new TeacherRoomManager with role "host"
    const role = p.streamInfo?.userId == p.teacher.id ? "host" : "audience";
    if (!s.manager) {
      const teacherRoomManagerParams: RoomOptions = {
        agora: {
          appId: p.streamInfo.appId,
          webConfig: { mode: "rtc", codec: "vp8", role: role },
          user: {
            channel: p.streamInfo.chanelId,
            username: p.streamInfo.userId,
            token: p.streamInfo.token,
            role: role,
          },
        },
      };
      s.manager = new TeacherRoomManager(teacherRoomManagerParams);
    }
    s.classAction = ClassActionFromValue(p.lessonPlan.lessonAction);
  },
  resetManagerClient(s: State): void {
    s.manager = undefined;
  },
  //setStudentsBadge
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENTS_BADGE)](s: State, p: TeacherUpdateStudentsBadgeEventData): void {
    p.forEach((item) => {
      const student = s.students.find((st) => st.id === item.studentId);
      if (student) student.badge = item.badge;
    });
  },
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_LEFT_CLASS)](s: State, p: UserIdPayload): void {
    const student = s.students.find((student) => student.id === p.id);
    if (student) {
      student.raisingHand = false;
      student.isPalette = false;
    }
  },
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_RAISING_HAND)](s: State, p: { id: string; raisingHand: boolean }): void {
    const student = s.students.find((student) => student.id === p.id);
    if (student) student.raisingHand = p.raisingHand;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_CLASS_ACTION)](state: State, payload: { action: ClassAction }) {
    state.classAction = payload.action;
  },
  //setStudentOneId
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_ONE_ID)](s: State, p: { id: string }) {
    s.idOne = p.id;
  },
  //setStudentOneWithAnotherTeacherId
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_ONE_WITH_ANOTHER_TEACHER_ID)](s: State, p: { id: string }) {
    s.idOneWithAnotherTeacher = p.id;
  },
  //setSpeakingUsers
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_SPEAKING_USERS)](s: State, p: { userIds: Array<string> }) {
    s.speakingUsers = p.userIds;
  },
  //setStudentPalette
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_PALETTE)](s: TeacherRoomState, p: { studentId: string; isPaletteEnabled: boolean }) {
    // If the student is a palette, then disable all other students' palette
    if (p.isPaletteEnabled) {
      s.students.forEach((student) => {
        if (student.id !== p.studentId) {
          student.isPalette = false;
        }
      });
    }
    // Then set the palette status of the student
    const student = s.students.find((st) => st.id === p.studentId);
    if (student) student.isPalette = p.isPaletteEnabled;
  },
  disableAllAnnotationStatus(s: TeacherRoomState, p: any) {
    s.students.forEach((student) => (student.isPalette = !p));
  },
  //setOnline
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_ONLINE)](state: TeacherRoomState) {
    state.isDisconnected = false;
  },
  //setOffline
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_OFFLINE)](state: TeacherRoomState) {
    state.isDisconnected = true;
  },
  //setListStudentLowBandWidth
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_LIST_STUDENT_LOW_BAND_WIDTH)](state: TeacherRoomState, p) {
    state.listStudentLowBandWidth = p;
  },
  //setWhiteboard
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_WHITEBOARD)](state: TeacherRoomState, p) {
    state.isShowWhiteboard = p;
  },
  setMediaState(state: TeacherRoomState, p) {
    state.mediaState = p;
  },
  //setAvatarTeacher
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_AVATAR_TEACHER)](state: TeacherRoomState, p: string) {
    if (state.teacher) state.teacher.avatar = p;
  },
  //setAvatarHelper
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_AVATAR_HELPER)](state: TeacherRoomState, p: string) {
    if (state.helper) state.helper.avatar = p;
  },
  //setTeacherDisconnected
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_TEACHER_DISCONNECTED)](state: TeacherRoomState, payload: boolean) {
    state.teacherIsDisconnected = payload;
    if (!payload && state.teacher) {
      state.teacher.disconnectTime = null;
    }
  },
  //setIsTeacherUseOnly
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_IS_TEACHER_USE_ONLY)](state: TeacherRoomState, p) {
    state.isTeacherUseOnly = p;
  },
  //setHelperInfo
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_HELPER_INFO)](s: TeacherRoomState, p: HelperModel | undefined) {
    if (!p) {
      s.helper = p;
    } else {
      s.helper = {
        id: p.id,
        name: p.name,
        avatar: s.helper?.avatar || "",
        videoCallStatus: p.videoCallStatus,
        isVideoShownByTeacher: p.isVideoShownByTeacher,
      };
    }
  },
  //setHelperVideoStatus
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_HELPER_VIDEO_STATUS)](s: TeacherRoomState, p: boolean) {
    if (s.helper) {
      s.helper.isVideoShownByTeacher = p;
    }
  },
  //setParamsForHelperJoinSession
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_PARAMS_FOR_HELPER_JOIN_SESSION)](s: TeacherRoomState, payload: ParamsToJoinCurSessionInterface) {
    s.paramsToJoinCurSession = payload;
  },
  //makeHelperTheNewTeacher
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.MAKE_HELPER_THE_NEW_TEACHER)](s: TeacherRoomState) {
    if (s.helper) {
      s.teacher = {
        ...cloneDeep(s.helper),
      };
      s.helper = undefined;
    }
  },
  //setIsSessionEnded
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_IS_SESSION_ENDED)](s: TeacherRoomState, loading: boolean) {
    s.isSessionEnded = loading;
  },
  //setInitialTeacherDisconnectStatus
  [defineVuexName(MODULES.TEACHER_ROOM.COMMITS.SET_INITIAL_TEACHER_DISCONNECT_STATUS)](s: TeacherRoomState) {
    if (s.teacher) {
      s.teacher.disconnectTime = 0;
      s.teacherIsDisconnected = true;
    }
  },
};

export default mutations;
